import { Box } from 'reflexbox';

import { tokens } from '../theme/tokens';

type Props = {
	values: number[];
	width?: number;
	height?: number;
	color?: string;
	// Draws a filled dot on the final datapoint.
	showEndDot?: boolean;
	strokeWidth?: number;
};

// Tiny inline trend line for table cells. Hand-drawn SVG polyline (no Chart.js)
// so a long table does not spin up a canvas per row.
export function Sparkline({
	values,
	width = 72,
	height = 24,
	color = tokens.brand.icon,
	showEndDot = true,
	strokeWidth = 1.5,
}: Props) {
	if (values.length === 0) return null;

	const pad = showEndDot ? strokeWidth + 2 : strokeWidth;
	const innerWidth = width - pad * 2;
	const innerHeight = height - pad * 2;

	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min || 1;

	const points = values.map((value, index) => {
		const x =
			values.length === 1 ? pad + innerWidth / 2 : pad + (index / (values.length - 1)) * innerWidth;
		// Invert y: SVG origin is top-left, higher scores should sit higher up.
		const y = pad + (1 - (value - min) / range) * innerHeight;
		return [x, y] as const;
	});

	const [lastX, lastY] = points[points.length - 1];

	return (
		<Box as="span" style={{ display: 'inline-flex', lineHeight: 0 }}>
			<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" aria-hidden>
				<polyline
					points={points.map(([x, y]) => `${x},${y}`).join(' ')}
					stroke={color}
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				{showEndDot && <circle cx={lastX} cy={lastY} r={strokeWidth + 0.5} fill={color} />}
			</svg>
		</Box>
	);
}
